import React, { useState } from 'react';
import Button from 'antd/lib/button';

interface IChartPaginationProps {
    totalItems: number;
    itemsPerPage: number;
    defaultCurrentPage: number;
    onChange: (page: number, itemsPerPage: number) => void;
}

/**
 * Inverted pagination where the last page is always shown by default and contains the most possible amount of items.
 * For example if we have 23 items and 10 items per page:
 * Page 1: 3 items
 * Page 2: 10 items
 * Page 3: 10 items
 */
export const ChartPagination = ({ totalItems, itemsPerPage, defaultCurrentPage, onChange }: IChartPaginationProps) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const [currentPage, setCurrentPage] = useState<number>(defaultCurrentPage);

    const handlePrev = () => {
        if (currentPage !== null && currentPage > 1) {
            const newPage = currentPage - 1;
            setCurrentPage(newPage);
            onChange(newPage, itemsPerPage);
        }
    };

    const handleNext = () => {
        if (currentPage !== null && currentPage < totalPages) {
            const newPage = currentPage + 1;
            setCurrentPage(newPage);
            onChange(newPage, itemsPerPage);
        }
    };

    if (totalItems <= itemsPerPage || !currentPage) return null;

    return (
        <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={handlePrev} disabled={currentPage === 1}>
                {`Previous${getPreviousItemsCount(currentPage, itemsPerPage, totalItems)}`}
            </Button>

            <Button onClick={handleNext} disabled={currentPage === totalPages}>
                {`Next${getNextItemsCount(currentPage, totalPages, itemsPerPage)}`}
            </Button>
        </div>
    );
};

function getNextItemsCount(currentPage: number, totalPages: number, itemsPerPage: number): string {
    if (currentPage >= totalPages) return '';

    return `(${(totalPages - currentPage) * itemsPerPage})`;
}

function getPreviousItemsCount(currentPage: number, itemsPerPage: number, totalItems: number): string {
    if (currentPage === 1) return '';

    const firstPageItems = totalItems % itemsPerPage || itemsPerPage;

    return `(${(currentPage - 1) * itemsPerPage - (itemsPerPage - firstPageItems)})`;
}
